import React, { useState, useEffect } from 'react';
import { PaymentPlan } from './PatientDetailsConstant';
import {
  formatPatientBalance,
  getBalanceColorClass,
} from 'src/legacy/utils/helpers';
import CustomModal from 'src/latest/src/components/CustomModal/CustomModal';
import AppButton from 'src/components/base/Tab/Button';
import { UserData } from 'src/latest/src/constants/UserData';
import Avatar from 'src/components/base/Avatar/Avatar';
import DropdownField from 'src/latest/src/components/DropdownComponent/DropdownField';
import { Choices } from 'src/latest/src/types/TherapistAvailabilityWidget';
import { getStripePaymentMethods } from 'src/redux/services/commonServices';
import 'src/latest/src/components/ChargePatientModal/ChargePatientModal.scss';
import { PaymentMethod } from 'src/containers/QuestionnaireParent/QuestionOptionsLibrary/components/StripePayment/StripePaymentInterface';
import {
  DirectoryRow,
  filterKey,
} from '../billing-admin-home/BIllingAdminConstant';
import Input from 'src/components/base/Input/Input';
import './PaymentPlanModal.scss';
import { Alert } from 'src/latest/src/components';
import { Logger } from 'src/latest/src/services';

interface PaymentPlanModalProps {
  show: boolean;
  mode: 'create' | 'edit';
  plan?: PaymentPlan;
  onClose: () => void;
  onSave: (planData: any) => void;
  userInfo: UserData | null;
  clientBalance: any;
  clientId?: string;
  isDirectoryPlatform: boolean;
  directoryPatientData: DirectoryRow | null;
}

const PaymentPlanModal: React.FC<PaymentPlanModalProps> = ({
  show,
  mode,
  plan,
  onClose,
  onSave,
  userInfo,
  clientBalance,
  clientId,
  isDirectoryPlatform,
  directoryPatientData,
}) => {
  const [paymentAmount, setPaymentAmount] = useState<string>('');
  const [frequency, setFrequency] = useState<string>('WEEKLY');
  const [startDate, setStartDate] = useState<string>('');
  const [isSaveDisabled, setIsSaveDisabled] = useState<boolean>(true);
  const [paymentMethods, setPaymentMethods] = useState<any>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    string | null
  >(null);
  const [errorAmount, setErrorAmount] = useState<string | undefined>();
  const [errorStartDate, setErrorStartDate] = useState<string | undefined>();
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (show && clientId) {
      fetchPaymentMethods();
    }
  }, [show, clientId]);

  const fetchPaymentMethods = async () => {
    if (!clientId) return;

    try {
      const response = await getStripePaymentMethods(clientId);

      setPaymentMethods(response);

      if (response?.payment_methods) {
        const defaultMethod = response.payment_methods.find(
          (method: any) => method.is_default,
        );

        if (defaultMethod) {
          setSelectedPaymentMethod(defaultMethod.id);
        }
      }
    } catch (error) {
      Logger.error('Error fetching payment methods:', error);
    }
  };

  const formatPaymentMethodsForDropdown = (): Choices[] => {
    if (!paymentMethods?.payment_methods) return [];

    const activeCards = paymentMethods?.payment_methods.filter(
      (method: PaymentMethod) => method.card_status === filterKey?.VALID_CARD,
    );

    const hasDefault = activeCards.some((m: PaymentMethod) => m.is_default);

    return activeCards.map((method: PaymentMethod) => ({
      label: (
        <span className="payment-method-option">
          <span className="payment-method-name">
            {method?.card_nick_name
              ? method?.card_nick_name
              : `${method.brand?.toUpperCase()}`}
          </span>

          <span className="payment-method-right">
            <span className="payment-method-separator">xx</span>
            <span className="payment-method-digits">
              {method?.last_4_digits}
            </span>
          </span>
        </span>
      ),
      searchText: `${method?.card_nick_name || method.brand} ${method.last_4_digits}`,
      value: method.id,
      selected: hasDefault ? method.is_default : false,
      unique_key: method.id,
    }));
  };

  const handlePaymentMethodChange = (choice: Choices) => {
    if (choice.value) {
      setSelectedPaymentMethod(choice.value);
    }
  };

  useEffect(() => {
    if (show) {
      if (mode === 'edit' && plan) {
        setPaymentAmount(plan.paymentAmount.toString());
        setFrequency(plan.frequency);
        setStartDate('');
      } else {
        setPaymentAmount('');
        setFrequency('WEEKLY');
        setStartDate('');
      }
    }
  }, [mode, plan, show]);

  useEffect(() => {
    if (mode === 'create') {
      setIsSaveDisabled(!paymentAmount || !frequency || !startDate);
    } else {
      setIsSaveDisabled(!paymentAmount || !frequency);
    }
  }, [paymentAmount, frequency, startDate, mode]);

  const handleSave = () => {
    setIsProcessing(true);
    const planData = {
      paymentAmount: parseFloat(paymentAmount),
      frequency,
      nextPaymentDate:
        mode === 'create'
          ? new Date(startDate).toISOString()
          : plan?.nextPaymentDate,
      maxFailures: 10,
      autoPauseOnFailure: true,
      paymentMethodId: selectedPaymentMethod,
    };

    onSave(planData);
    setIsProcessing(false);
  };

  const handlePaymentAmountChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = e.target.value;

    setPaymentAmount(value);

    if (parseFloat(value) <= 0 || value === '') {
      setErrorAmount('Amount must be greater than 0');
    } else {
      setErrorAmount('');
    }
  };

  const formatFrequencyForDropdown = (): Choices[] => {
    const frequencies = [
      { label: 'Weekly', value: 'WEEKLY' },
      { label: 'Bi-weekly', value: 'BIWEEKLY' },
      { label: 'Monthly', value: 'MONTHLY' },
    ];

    return frequencies.map(freq => ({
      label: freq.label,
      value: freq.value,
      selected: freq.value === frequency,
      unique_key: freq.value,
    }));
  };

  const handleFrequencyChange = (choice: Choices) => {
    if (choice.value) {
      setFrequency(choice.value);
    }
  };

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    setStartDate(value);

    if (!value) {
      setErrorStartDate('Start date is required');
    } else {
      setErrorStartDate('');
    }
  };

  const getBalanceValue = () => {
    if (isDirectoryPlatform) {
      return directoryPatientData?.provider_balance || 0;
    } else {
      return clientBalance?.patient_balance_amount || 0;
    }
  };

  const isBalanceZeroOrNegative = getBalanceValue() <= 0;

  const handleOnClose = () => {
    setErrorAmount(undefined);
    setErrorStartDate(undefined);
    onClose();
  };

  return (
    <CustomModal
      showModal={show}
      setShowModal={handleOnClose}
      headerText={
        mode === 'create' ? 'Create Payment Plan' : 'Edit Payment Plan'
      }
      backdrop={'static'}
      dialogClassName="payment-plan-modal-dialog"
      headerClassName="payment-plan-modal-title"
      className="payment-plan-modal-body"
      iconClassName="payment-plan-modal-img"
    >
      <div className="payment-plan-modal">
        <div className="payment-plan-modal-detail">
          <div className="payment-plan-modal-detail-info">
            {userInfo?.profile_pic && userInfo?.profile_pic?.length > 0 && (
              <div className="payment-plan-modal-detail-avatar">
                <Avatar photoUrl={userInfo?.profile_pic} size="38px" />
              </div>
            )}
            <div className="payment-plan-modal-detail-name">
              <div className="payment-plan-modal-detail-name-text">
                {userInfo?.first_name} {userInfo?.last_name}
              </div>
              <div className="payment-plan-modal-detail-name-phone">
                {userInfo?.telephone_number}
              </div>
            </div>
          </div>
          <div className="payment-plan-modal-detail-balance">
            <div
              className={`payment-plan-modal-detail-balance-dollar ${getBalanceColorClass(
                isDirectoryPlatform
                  ? directoryPatientData?.provider_balance
                  : clientBalance?.patient_balance_amount,
              )}`}
            >
              {formatPatientBalance(
                isDirectoryPlatform
                  ? directoryPatientData?.provider_balance
                  : clientBalance?.patient_balance_amount,
              )}
            </div>
            <div className="payment-plan-modal-detail-balance-text">
              {isDirectoryPlatform ? 'Provider' : 'Patient'} Balance
            </div>
          </div>
        </div>
        <div className="payment-plan-modal-amount">
          <span className="payment-plan-modal-amount-label">Charge Amount</span>
          <Input
            name={`payment-plan-amount`}
            value={paymentAmount}
            onChange={handlePaymentAmountChange}
            type="number"
            placeholder={`Enter amount`}
            isInvalid={!!errorAmount}
            className={`mx-auto`}
            feedbackMessage={errorAmount}
            isCheckIcon={false}
          />
        </div>
        <div>
          {paymentMethods?.payment_methods &&
            paymentMethods.payment_methods.length > 0 && (
              <div className="payment-plan-modal-amount">
                <span className="payment-plan-modal-amount-label">
                  Payment Method
                </span>
                <div className="payment-plan-modal-payment-method-dropdown">
                  <DropdownField
                    className="payment-plan-dropdown"
                    title={'Select Payment Method'}
                    onChangeValue={handlePaymentMethodChange}
                    menu={formatPaymentMethodsForDropdown()}
                  />
                </div>
              </div>
            )}
        </div>

        <div className="payment-plan-modal-amount">
          <span className="payment-plan-modal-amount-label">Frequency</span>
          <div className="payment-plan-modal-payment-method-dropdown">
            <DropdownField
              className="payment-plan-dropdown"
              title={'Select Frequency'}
              onChangeValue={handleFrequencyChange}
              menu={formatFrequencyForDropdown()}
            />
          </div>
        </div>
        {mode === 'create' && (
          <div className="payment-plan-modal-amount">
            <span className="payment-plan-modal-amount-label">Start Date</span>
            <Input
              name={`payment-plan-start-date`}
              value={startDate}
              onChange={handleStartDateChange}
              type="date"
              placeholder={`Enter start date`}
              isInvalid={!!errorStartDate}
              className={`mx-auto`}
              feedbackMessage={errorStartDate}
              isCheckIcon={false}
            />
          </div>
        )}
        {isBalanceZeroOrNegative && (
          <Alert
            type="error"
            className="payment-plan-modal-error"
            message={'Cannot create payment plan with zero or negative balance'}
          />
        )}
        <div className="payment-plan-modal-btn">
          <div>
            <AppButton
              className="close-btn payment-modal-btn"
              label={'Cancel'}
              onClick={handleOnClose}
              variant="secondary"
            />
          </div>
          <div>
            <AppButton
              className="btn-new-primary payment-modal-btn"
              label={`${mode === 'edit' ? 'Update' : 'Save'} `}
              onClick={handleSave}
              disabled={isSaveDisabled || isBalanceZeroOrNegative}
              isProcessing={isProcessing}
            />
          </div>
        </div>
      </div>
    </CustomModal>
  );
};

export default PaymentPlanModal;
